import { isNative } from "@/lib/capacitor";

/**
 * Share a report blob via the OS share sheet on native, or trigger a browser
 * download on web. The caller is responsible for picking a filename whose
 * extension matches the blob's actual MIME type.
 *
 * On native: writes to Filesystem.Cache, then invokes the iOS share sheet.
 * On web: creates an anchor tag with `download` and clicks it.
 *
 * Errors on the native path are surfaced; "user cancelled" is a non-error
 * silent return (caller should not toast).
 */
export async function shareReportBlob(blob: Blob, filename: string): Promise<void> {
  if (!isNative) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 0);
    return;
  }

  const [{ Filesystem, Directory }, { Share }] = await Promise.all([
    import("@capacitor/filesystem"),
    import("@capacitor/share"),
  ]);

  const base64 = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const r = reader.result as string;
      resolve(r.split(",")[1]);
    };
    reader.onerror = () => reject(reader.error ?? new Error("FileReader failed"));
    reader.readAsDataURL(blob);
  });

  const written = await Filesystem.writeFile({
    path: filename,
    data: base64,
    directory: Directory.Cache,
  });

  try {
    await Share.share({
      title: filename,
      files: [written.uri],
      dialogTitle: "Save report",
    });
  } catch (err: any) {
    // Capacitor's Share plugin throws when the user cancels. Recognize and swallow.
    const msg = err?.message ?? "";
    if (msg.toLowerCase().includes("cancel")) return;
    throw err;
  }
}
